import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  ButtonElementSchema,
  ColumnElementSchema,
  ContainerElementSchema,
  DiscountCodeElementSchema,
  ImageElementSchema,
  InputElementSchema,
  ProductElementSchema,
  RichTextElementSchema,
  TextToSubscribeButtonElementSchema,
  TextToSubscribeDescriptionElementSchema,
} from "@redotech/redo-model/content-builder/element-schema";

// ============== SPOTLIGHT TARGETING TYPES ==============

export enum SpotlightTargetingType {
  ALL_VISITORS = "all visitors",
  EXCLUDE_EXISTING_CONTACTS = "exclude existing contacts",
  ONLY_EXISTING_CONTACTS = "only existing contacts",
}

// ============== SPOTLIGHT DEVICE VISIBILITY ==============

export enum SpotlightDeviceVisibility {
  BOTH = "both",
  DESKTOP_ONLY = "desktop-only",
  MOBILE_ONLY = "mobile-only",
}

// ============== SPOTLIGHT TRIGGER TYPES ==============

export enum SpotlightTriggerType {
  EXIT_INTENT = "exitIntent",
  TIME_ON_PAGE = "timeOnPage",
  CHECKOUT_PROBABILITY = "checkoutProbability",
  SHOPPER_INDECISION = "shopperIndecision",
  BACK_IN_STOCK = "backInStock",
}

// ============== SPOTLIGHT TRIGGERS ==============

const TimeOnPageTriggerSchema_ = z.object({
  enabled: z.boolean(),
  seconds: z.number(),
});
export const TimeOnPageTriggerSchema: z.ZodType<TimeOnPageTrigger> =
  TimeOnPageTriggerSchema_;
export interface TimeOnPageTrigger
  extends z.infer<typeof TimeOnPageTriggerSchema_> {}

const SpotlightTriggersSchema_ = z.object({
  checkoutProbability: z.object({ enabled: z.boolean() }),
  shopperIndecision: z.object({ enabled: z.boolean() }),
  timeOnPage: TimeOnPageTriggerSchema_,
  exitIntent: z.object({ enabled: z.boolean() }),
  backInStock: z.object({ enabled: z.boolean() }).default({ enabled: false }),
});
export const SpotlightTriggersSchema: z.ZodType<SpotlightTriggers> =
  SpotlightTriggersSchema_;
export interface SpotlightTriggers
  extends z.infer<typeof SpotlightTriggersSchema_> {}

/**
 * Extracts the enabled trigger type from a SpotlightTriggers object.
 * Returns null if no trigger is enabled.
 */
export function getSpotlightTriggerType(
  triggers: SpotlightTriggers,
): SpotlightTriggerType | null {
  if (triggers.exitIntent.enabled) {
    return SpotlightTriggerType.EXIT_INTENT;
  }
  if (triggers.timeOnPage.enabled) {
    return SpotlightTriggerType.TIME_ON_PAGE;
  }
  if (triggers.checkoutProbability.enabled) {
    return SpotlightTriggerType.CHECKOUT_PROBABILITY;
  }
  if (triggers.shopperIndecision.enabled) {
    return SpotlightTriggerType.SHOPPER_INDECISION;
  }
  if (triggers.backInStock.enabled) {
    return SpotlightTriggerType.BACK_IN_STOCK;
  }
  return null;
}

// ============== PAGE RULES ==============

export enum PageRuleType {
  INCLUDE = "include",
  EXCLUDE = "exclude",
}

const PageRuleSchema_ = z.object({
  url: z.string().min(1),
  type: createEnum(PageRuleType),
});
export const PageRuleSchema: z.ZodType<PageRule> = PageRuleSchema_;
export interface PageRule extends z.infer<typeof PageRuleSchema_> {}

export enum MobileStepType {
  FULLSCREEN = "fullscreen",
  DRAWER = "drawer",
}

export enum DesktopStepType {
  POPUP = "popup",
  FULLSCREEN = "fullscreen",
  PANEL = "panel",
}

export enum PopupPlacement {
  CENTER = "center",
  TOP_LEFT = "top-left",
  TOP_CENTER = "top-center",
  TOP_RIGHT = "top-right",
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_CENTER = "bottom-center",
  BOTTOM_RIGHT = "bottom-right",
}

export enum PanelPlacement {
  LEFT = "left",
  RIGHT = "right",
  TOP = "top",
  BOTTOM = "bottom",
}

export enum ImageLayout {
  TOP = "top",
  RIGHT = "right",
  BOTTOM = "bottom",
  LEFT = "left",
  NONE = "none",
  BACKGROUND = "background",
}

export enum SpotlightSize {
  S = "S",
  M = "M",
  L = "L",
}

const PaddingSchema = z.object({
  top: z.number(),
  right: z.number(),
  bottom: z.number(),
  left: z.number(),
});

const BorderSchema = z.object({ color: z.string(), weight: z.number() });

const AdvancedSettingsBaseSchema = z.object({
  background: z.string(),
  padding: PaddingSchema,
});

const AdvancedSettingsWithBorderSchema = AdvancedSettingsBaseSchema.extend({
  border: BorderSchema,
  cornerRadius: z.number(),
  overlay: z.string(),
});

const AdvancedSettingsWithWidthSchema = AdvancedSettingsWithBorderSchema.extend(
  { width: z.number() },
);

// ============== LAUNCHER ADVANCED SETTINGS ==============

const LauncherAdvancedSettingsWithBorderSchema =
  AdvancedSettingsWithBorderSchema.extend({
    fontColor: z.string(),
    fontSize: z.number(),
  });

const LauncherAdvancedSettingsWithWidthSchema =
  LauncherAdvancedSettingsWithBorderSchema.extend({ width: z.number() });

// ============== MOBILE STEP CONFIGS ==============

const MobileFullscreenConfigSchema_ = z.object({
  type: z.literal(MobileStepType.FULLSCREEN),
  advancedSettings: AdvancedSettingsBaseSchema,
});
export const MobileFullscreenConfigSchema: z.ZodType<MobileFullscreenConfig> =
  MobileFullscreenConfigSchema_;
export interface MobileFullscreenConfig
  extends z.infer<typeof MobileFullscreenConfigSchema_> {}

const MobileDrawerConfigSchema_ = z.object({
  type: z.literal(MobileStepType.DRAWER),
  size: createEnum(SpotlightSize),
  advancedSettings: AdvancedSettingsWithBorderSchema,
});
export const MobileDrawerConfigSchema: z.ZodType<MobileDrawerConfig> =
  MobileDrawerConfigSchema_;
export interface MobileDrawerConfig
  extends z.infer<typeof MobileDrawerConfigSchema_> {}

export const MobileStepConfigSchema = z.discriminatedUnion("type", [
  MobileFullscreenConfigSchema_,
  MobileDrawerConfigSchema_,
]);
export type MobileStepConfig = z.infer<typeof MobileStepConfigSchema>;

// ============== DESKTOP STEP CONFIGS ==============

const DesktopPopupConfigSchema_ = z.object({
  type: z.literal(DesktopStepType.POPUP),
  placement: createEnum(PopupPlacement),
  size: createEnum(SpotlightSize),
  advancedSettings: AdvancedSettingsWithBorderSchema,
});
export const DesktopPopupConfigSchema: z.ZodType<DesktopPopupConfig> =
  DesktopPopupConfigSchema_;
export interface DesktopPopupConfig
  extends z.infer<typeof DesktopPopupConfigSchema_> {}

const DesktopFullscreenConfigSchema_ = z.object({
  type: z.literal(DesktopStepType.FULLSCREEN),
  advancedSettings: AdvancedSettingsBaseSchema,
});
export const DesktopFullscreenConfigSchema: z.ZodType<DesktopFullscreenConfig> =
  DesktopFullscreenConfigSchema_;
export interface DesktopFullscreenConfig
  extends z.infer<typeof DesktopFullscreenConfigSchema_> {}

const DesktopPanelConfigSchema_ = z.object({
  type: z.literal(DesktopStepType.PANEL),
  placement: createEnum(PanelPlacement),
  size: createEnum(SpotlightSize),
  advancedSettings: AdvancedSettingsWithWidthSchema,
});
export const DesktopPanelConfigSchema: z.ZodType<DesktopPanelConfig> =
  DesktopPanelConfigSchema_;
export interface DesktopPanelConfig
  extends z.infer<typeof DesktopPanelConfigSchema_> {}

export const DesktopStepConfigSchema = z.discriminatedUnion("type", [
  DesktopPopupConfigSchema_,
  DesktopFullscreenConfigSchema_,
  DesktopPanelConfigSchema_,
]);
export type DesktopStepConfig = z.infer<typeof DesktopStepConfigSchema>;

export enum DeviceView {
  BOTH = "both",
  MOBILE = "mobile",
  DESKTOP = "desktop",
}

// ============== SPOTLIGHT ELEMENTS (Reuse MarketingForge) ==============

const SpotlightElementSchema = z.union([
  ImageElementSchema,
  ProductElementSchema,
  ColumnElementSchema,
  ContainerElementSchema,
  RichTextElementSchema,
  ButtonElementSchema,
  InputElementSchema,
  DiscountCodeElementSchema,
  TextToSubscribeButtonElementSchema,
  TextToSubscribeDescriptionElementSchema,
]);

export type SpotlightElement = z.infer<typeof SpotlightElementSchema>;

// ============== SPOTLIGHT STEP ==============

const SpotlightStepSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  elements: z.array(SpotlightElementSchema),
  mobile: MobileStepConfigSchema,
  desktop: DesktopStepConfigSchema,
});
export const SpotlightStepSchema: z.ZodType<SpotlightStep> =
  SpotlightStepSchema_;
export interface SpotlightStep extends z.infer<typeof SpotlightStepSchema_> {}

// ============== LAUNCHER TYPES ==============

export enum LauncherType {
  TAB = "tab",
  EMBED = "embed",
  BANNER = "banner",
}

export enum TabPlacement {
  TOP_LEFT = "top-left",
  CENTER_LEFT = "center-left",
  BOTTOM_LEFT = "bottom-left",
  TOP_RIGHT = "top-right",
  CENTER_RIGHT = "center-right",
  BOTTOM_RIGHT = "bottom-right",
}

export enum TabOrientation {
  VERTICAL = "vertical",
  HORIZONTAL = "horizontal",
}

// ============== LAUNCHER CONFIGS ==============

const TabLauncherConfigSchema_ = z.object({
  type: z.literal(LauncherType.TAB),
  text: z.string(),
  placement: createEnum(TabPlacement),
  orientation: createEnum(TabOrientation),
  showBeforePopupTriggered: z.boolean(),
  advancedSettings: LauncherAdvancedSettingsWithWidthSchema,
});
export const TabLauncherConfigSchema: z.ZodType<TabLauncherConfig> =
  TabLauncherConfigSchema_;
export interface TabLauncherConfig
  extends z.infer<typeof TabLauncherConfigSchema_> {}

const EmbedLauncherConfigSchema_ = z.object({
  type: z.literal(LauncherType.EMBED),
  text: z.string(),
  anchorCssSelector: z.string(),
  showBeforePopupTriggered: z.boolean(),
  advancedSettings: LauncherAdvancedSettingsWithWidthSchema,
});
export const EmbedLauncherConfigSchema: z.ZodType<EmbedLauncherConfig> =
  EmbedLauncherConfigSchema_;
export interface EmbedLauncherConfig
  extends z.infer<typeof EmbedLauncherConfigSchema_> {}

const BannerLauncherConfigSchema_ = z.object({
  type: z.literal(LauncherType.BANNER),
  text: z.string(),
  showBeforePopupTriggered: z.boolean(),
  advancedSettings: LauncherAdvancedSettingsWithBorderSchema,
});
export const BannerLauncherConfigSchema: z.ZodType<BannerLauncherConfig> =
  BannerLauncherConfigSchema_;
export interface BannerLauncherConfig
  extends z.infer<typeof BannerLauncherConfigSchema_> {}

export const SpotlightLauncherSchema = z.discriminatedUnion("type", [
  TabLauncherConfigSchema_,
  EmbedLauncherConfigSchema_,
  BannerLauncherConfigSchema_,
]);
export type SpotlightLauncher = z.infer<typeof SpotlightLauncherSchema>;

const SpotlightFormSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  steps: z.array(SpotlightStepSchema),
  launcher: SpotlightLauncherSchema.optional(),
  deviceVisibility: createEnum(SpotlightDeviceVisibility),
  triggers: SpotlightTriggersSchema_,
  targeting: createEnum(SpotlightTargetingType),
  pageRules: z.array(PageRuleSchema_),
  completionTags: z.array(z.string()),
});
export const SpotlightFormSchema: z.ZodType<SpotlightForm> =
  SpotlightFormSchema_;
export interface SpotlightForm extends z.infer<typeof SpotlightFormSchema_> {}

// ============== SPOTLIGHT A/B TESTING SCHEMAS ==============

export const spotlightDeviceTarget = [
  "desktop",
  "mobile",
  "desktop & mobile",
] as const;
export type SpotlightDeviceTarget = (typeof spotlightDeviceTarget)[number];

const spotlightSplitDBSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  active: z.boolean(),
  startDate: z.date(),
  deviceTarget: createEnum(spotlightDeviceTarget, {
    enumName: "SpotlightDeviceTarget",
  }),
  triggerType: createEnum(SpotlightTriggerType),
  treatments: z.array(zExt.objectId()),
  deleted: z.boolean(),
});
export const spotlightSplitDBSchema: z.ZodType<SpotlightSplitDBSchema> =
  spotlightSplitDBSchema_;
export interface SpotlightSplitDBSchema
  extends z.infer<typeof spotlightSplitDBSchema_> {}

const spotlightSplitTreatment_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  spotlightId: zExt.objectId(),
  weight: z.number(),
});
export const spotlightSplitTreatment: z.ZodType<SpotlightSplitTreatment> =
  spotlightSplitTreatment_;
export interface SpotlightSplitTreatment
  extends z.infer<typeof spotlightSplitTreatment_> {}

const spotlightSplitTreatmentWithMetrics_ = spotlightSplitTreatment_.extend({
  views: z.number(),
  engagements: z.number(),
  engagementRate: z.number(),
  completions: z.number(),
  completionRate: z.number(),
  emailSignups: z.number(),
  smsSignups: z.number(),
});
export const spotlightSplitTreatmentWithMetrics: z.ZodType<SpotlightSplitTreatmentWithMetrics> =
  spotlightSplitTreatmentWithMetrics_;
export interface SpotlightSplitTreatmentWithMetrics
  extends z.infer<typeof spotlightSplitTreatmentWithMetrics_> {}

const spotlightSplitBase_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  active: z.boolean(),
  startDate: z.date(),
  deviceTarget: createEnum(spotlightDeviceTarget, {
    enumName: "SpotlightDeviceTarget",
  }),
  triggerType: createEnum(SpotlightTriggerType),
});

const spotlightSplit_ = spotlightSplitBase_.extend({
  treatments: z.array(spotlightSplitTreatment),
});
export const spotlightSplit: z.ZodType<SpotlightSplit> = spotlightSplit_;
export interface SpotlightSplit extends z.infer<typeof spotlightSplit_> {}

const spotlightSplitWithMetrics_ = spotlightSplitBase_.extend({
  treatments: z.array(spotlightSplitTreatmentWithMetrics),
});
export const spotlightSplitWithMetrics: z.ZodType<SpotlightSplitWithMetrics> =
  spotlightSplitWithMetrics_;
export interface SpotlightSplitWithMetrics
  extends z.infer<typeof spotlightSplitWithMetrics_> {}

export const spotlightAbTestVariantSchema = z.object({
  spotlightId: z.string(),
  weight: z.number().min(0).max(100),
  name: z.string().min(1, "Variant name is required"),
});

export const createSpotlightAbTestFormParser = z.object({
  name: z.string().min(1, "Test name is required"),
  variants: z
    .array(spotlightAbTestVariantSchema)
    .min(2, "At least 2 variants required")
    .max(3, "Maximum 3 variants allowed per test")
    .refine(
      (variants) => {
        const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
        return totalWeight === 100;
      },
      { message: "Variant weights must sum to 100%" },
    ),
});

const spotlightAnalytics_ = z.object({
  spotlightId: z.string(),
  views: z.number(),
  engagements: z.number(),
  engagementRate: z.number(),
  completions: z.number(),
  completionRate: z.number(),
  emailSignups: z.number(),
  smsSignups: z.number(),
});
export const spotlightAnalytics: z.ZodType<SpotlightAnalytics> =
  spotlightAnalytics_;
export interface SpotlightAnalytics
  extends z.infer<typeof spotlightAnalytics_> {}
